<template>
  <TableList
    v-rect-loading="loading"
    class="candidate-status-list"
    row-key="key"
    :titleI18nKey="`${i18nPrefix}.menu`"
    :list-data="statusList"
    :table-columns="tableColumns"
    :actionBtn="actionBtn"
    @create="handleCreate"
    @dragSort="changeSort"
  >
    <template #filter>
      <el-select v-model="search.disabled" clearable filterable @change="handleDisabledStatusChange">
        <el-option
          v-for="(item, index) in statusOptionList"
          :key="index"
          :label="item.label"
          :value="item.value"
        ></el-option>
      </el-select>
      <el-input v-model="search.keyword" clearable suffix-icon="el-icon-search" :placeholder="$t(`${i18nPrefix}.placeholder.keywords`)" @input="handleKeywordsChange"></el-input>
    </template>
    <template #btn-before>
      <el-button v-if="hasChangeSort" class="save-btn" @click="handleSaveSort">{{$t('common.btn.save')}}</el-button>
    </template>
    <template #isEnabled="{ data, row }">
      <el-switch v-if="row.can_edit" :value="data" @change="onEnabledChange($event, row)"></el-switch>
      <div v-else></div>
    </template>
    <template #draggable>
      <div class="drag-btn">
        <i class="iconfont iconMore draggable-handle"></i>
      </div>
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'

export default {
  components: {
    TableList
  },
  data () {
    return {
      i18nPrefix: 'admin.company.candidateStatusManagement',
      loading: false,
      statusList: [],
      actionBtn: {
        edit: {
          show: (row) => row.can_edit,
          onclick: this.handleEdit
        }
      },
      search: {
        disabled: '0',
        keyword: null
      },
      oldStatusKeyList: []
    }
  },
  computed: {
    tableColumns () {
      const columns = [
        {
          label: this.$t(`${this.i18nPrefix}.label.cnName`),
          dataKey: 'name_cn',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.enName`),
          dataKey: 'name_en',
          showOverflowTooltip: true
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.statusEnableLabel`),
          dataKey: 'is_enabled',
          slotName: 'isEnabled'
        },
        {
          hidden: this.search.disabled !== '0' || this.search.keyword,
          label: this.$t('common.label.sortOperate'),
          isDragSort: true,
          slotName: 'draggable',
          width: 50
        }
      ]
      return columns
    },
    statusOptionList () {
      return [
        {
          value: '0',
          label: this.$t(`${this.i18nPrefix}.label.enabled`)
        },
        {
          value: '1',
          label: this.$t(`${this.i18nPrefix}.label.disabled`)
        }
      ]
    },
    hasChangeSort () {
      const newStatusKeyList = _.map(this.statusList, 'key')
      return !_.isEqual(this.oldStatusKeyList, newStatusKeyList)
    }
  },
  created () {
    this.initPage()
  },
  mounted () {
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
  },
  destroyed () {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
  },
  methods: {
    ...mapActions('candidateStatus', ['getAllCandidateStatusList', 'updateCandidateStatus', 'updateCandidateStatusOrder']),
    beforeUnloadHandler (e) {
      if (this.hasChangeSort) {
        // 必须要这段代码阻止默认行为，提示的弹窗才会出来
        e.preventDefault()
        e.returnValue = ''
      }
    },
    initPage () {
      this.setSearch()
      this.loadCandidateStatusList()
    },
    async loadCandidateStatusList () {
      const params = {
        is_disabled: this.search.disabled,
        keyword: this.search.keyword
      }
      this.loading = true
      try {
        const resp = await this.getAllCandidateStatusList(params)
        if (resp) {
          this.statusList = resp
          this.oldStatusKeyList = _.map(resp, 'key')
        }
      } finally {
        this.loading = false
      }
    },
    setSearch () {
      if (!this.$route.query) {
        return
      }
      const search = this.$route.query
      if (_.isEmpty(search)) {
        return
      }
      this.search.keyword = search.keyword
      this.search.disabled = search.disabled
    },
    handleDisabledStatusChange () {
      this.$router.replace({ query: this.search })
      this.loadCandidateStatusList()
    },
    handleKeywordsChange () {
      if (!this.keywordsSearchDebounce) {
        this.keywordsSearchDebounce = _.debounce(this.loadCandidateStatusList, 1000)
      }
      this.$router.replace({ query: this.search })
      this.keywordsSearchDebounce()
    },
    handleCreate () {
      this.$router.push({ path: '/v-admin/client-admin/candidate-status-management/add', query: this.search })
    },
    handleEdit (row) {
      this.$router.push({ path: `/v-admin/client-admin/candidate-status-management/edit/${row.key}`, query: this.search })
    },
    async changeSort (newIndex, oldIndex) {
      if (oldIndex !== newIndex) {
        const oldStatus = this.statusList[oldIndex]
        if (oldStatus) {
          oldStatus.order_num = newIndex
        }
        const newStatus = this.statusList[newIndex]
        if (newStatus) {
          newStatus.order_num = oldIndex
        }
        this.resetStatusSort()
      }
    },
    resetStatusSort () {
      _.forEach(this.statusList, (status, index) => {
        status.order_num = index
      })
    },
    async handleSaveSort () {
      const statusKeys = _.map(this.statusList, status => status.key)
      const params = {
        status_keys: statusKeys
      }
      this.loading = true
      try {
        await this.updateCandidateStatusOrder(params)
        this.loadCandidateStatusList()
      } finally {
        this.loading = false
      }
    },
    async onEnabledChange (isEnabled, row) {
      const params = {
        status_key: row.key,
        name_cn: row.name_cn,
        name_en: row.name_en,
        is_enabled: isEnabled
      }
      this.loading = true
      try {
        const resp = await this.updateCandidateStatus(params)
        if (resp) {
          row.is_enabled = isEnabled
          this.$message({
            type: 'success',
            message: this.$t(`${this.i18nPrefix}.message.updateStatusSuccess`)
          })
          this.loadCandidateStatusList()
        }
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
</style>
